import { useState, useEffect, useCallback, useRef } from "react";
import { uploadQueue, type QueueStatus } from "@/lib/uploadQueue";

/**
 * React hook for the persistent upload queue.
 * Provides reactive state and actions for the upload queue.
 */
export function useUploadQueue(onBatchComplete?: (items: Array<{ folder: string; projectId?: number; leadId?: number; fileName: string }>) => void) {
  const [status, setStatus] = useState<QueueStatus>({
    items: [],
    activeCount: 0,
    totalCount: 0,
    doneCount: 0,
    errorCount: 0,
    isProcessing: false,
  });

  // Keep callback ref stable to avoid re-subscribing
  const batchCallbackRef = useRef(onBatchComplete);
  batchCallbackRef.current = onBatchComplete;

  useEffect(() => {
    // Initialize the queue manager
    uploadQueue.init();

    // Subscribe to status updates
    const unsubscribe = uploadQueue.subscribe((newStatus) => {
      setStatus(newStatus);
    });

    // Subscribe to batch completion events
    const unsubBatch = uploadQueue.onBatchComplete((items) => {
      batchCallbackRef.current?.(items);
    });

    return () => {
      unsubscribe();
      unsubBatch();
    };
  }, []);

  const addFiles = useCallback(
    (files: File[], options: { folder: string; leadId?: number; projectId?: number }) => {
      return uploadQueue.addFiles(files, options);
    },
    []
  );

  const retryFailed = useCallback(() => {
    return uploadQueue.retryFailed();
  }, []);

  const clearCompleted = useCallback(() => {
    uploadQueue.clearCompleted();
  }, []);

  return {
    ...status,
    addFiles,
    retryFailed,
    clearCompleted,
    hasItems: status.totalCount > 0,
    hasPending: status.totalCount - status.doneCount - status.errorCount > 0,
  };
}
